/**
 * VirtHypervisorForm — Odoo-style full-screen detail view
 * Tabs: General / Hardware / Máquinas Virtuales
 * SmartButtons: tipo, IP gestión, servidor físico, VMs, estado
 */

import React, { useEffect, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  CpuChipIcon,
  ComputerDesktopIcon,
  ServerStackIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const HYP_TYPES = [
  { value: 'vmware_esxi',  label: 'VMware ESXi',      icon: '🟦' },
  { value: 'proxmox',      label: 'Proxmox VE',        icon: '🟧' },
  { value: 'hyper_v',      label: 'Hyper-V',           icon: '🪟' },
  { value: 'kvm',          label: 'KVM / Libvirt',      icon: '🐧' },
  { value: 'nutanix_ahv',  label: 'Nutanix AHV',       icon: '🔴' },
  { value: 'xen',          label: 'Xen / XCP-ng',      icon: '🟩' },
  { value: 'other',        label: 'Otro',              icon: '⬜' },
];

const STATUS_COLORS = {
  active:      { bg: 'rgba(16,185,129,.15)',  color: '#6ee7b7', label: 'Activo' },
  maintenance: { bg: 'rgba(234,179,8,.15)',   color: '#fde68a', label: 'Mantenimiento' },
  decom:       { bg: 'rgba(239,68,68,.15)',   color: '#fca5a5', label: 'Decom' },
};

const EMPTY_FORM = {
  name: '', hypervisor_type: 'vmware_esxi', version: '',
  mgmt_ip: '', mgmt_url: '', ha_mode: 'standalone',
  status: 'active', notes: '',
  cpu_total_cores: '', cpu_model: '', ram_total_gb: '', storage_local_tb: '',
  rack_element_id: '',
};

export default function VirtHypervisorForm({ hypervisor, companyId, onClose, onSave, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(hypervisor?.id);
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);

  // ── Data ──
  const [servers, setServers] = useState([]);
  const [vms, setVMs] = useState([]);

  // ── Form ──
  const [form, setForm] = useState(EMPTY_FORM);
  const [isDirty, setIsDirty] = useState(false);

  // ── UI ──
  const [activeTab, setActiveTab] = useState('general');
  const [accessCount, setAccessCount] = useState(0);
  const [saving, setSaving] = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este hipervisor',
    actionLabel: 'pasas a otro hipervisor o cierras la ficha',
    save: () => handleSubmit(null, { keepEditing: editing }),
  });
  const [savedOk, setSavedOk] = useState(false);
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState([]);

  // ── Load supporting data ──
  useEffect(() => {
    if (!companyId) return;
    api.getServers(companyId)
      .then(r => setServers(r.data || []))
      .catch(() => {});
  }, [companyId]);

  // Load VMs for this hypervisor
  useEffect(() => {
    if (!hypervisor?.id || !companyId) return;
    api.getVMs(companyId)
      .then(r => setVMs((r.data || []).filter(v => v.hypervisor_id === hypervisor.id)))
      .catch(() => {});
  }, [hypervisor?.id, companyId]);

  // ── Populate form from prop ──
  useEffect(() => {
    if (!hypervisor) { setForm(EMPTY_FORM); return; }
    setForm({
      name:            hypervisor.name || '',
      hypervisor_type: hypervisor.hypervisor_type || 'vmware_esxi',
      version:         hypervisor.version || '',
      mgmt_ip:         hypervisor.mgmt_ip || '',
      mgmt_url:        hypervisor.mgmt_url || '',
      ha_mode:         hypervisor.ha_mode || 'standalone',
      status:          hypervisor.status || 'active',
      notes:           hypervisor.notes || '',
      cpu_total_cores: hypervisor.cpu_total_cores ?? '',
      cpu_model:       hypervisor.cpu_model || '',
      ram_total_gb:    hypervisor.ram_total_gb ?? '',
      storage_local_tb: hypervisor.storage_local_tb ?? '',
      rack_element_id: hypervisor.rack_element_id ?? '',
    });
  }, [hypervisor]);

  // ── Derived ──
  const typeInfo = HYP_TYPES.find(t => t.value === form.hypervisor_type) || HYP_TYPES[HYP_TYPES.length - 1];
  const currentStatus = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const selectedServer = servers.find(s => String(s.id) === String(form.rack_element_id));
  const hwSummary = [
    form.cpu_total_cores && `${form.cpu_total_cores} cores`,
    form.ram_total_gb && `${form.ram_total_gb} GB RAM`,
    form.storage_local_tb && `${form.storage_local_tb} TB`,
  ].filter(Boolean).join(' · ');

  // ── Handlers ──
  const sf = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setIsDirty(true);
    setApiError('');
    setErrors([]);
  };

  const handleDiscard = () => {
    if (hypervisor) {
      setForm({
        name: hypervisor.name || '', hypervisor_type: hypervisor.hypervisor_type || 'vmware_esxi',
        version: hypervisor.version || '', mgmt_ip: hypervisor.mgmt_ip || '',
        mgmt_url: hypervisor.mgmt_url || '', ha_mode: hypervisor.ha_mode || 'standalone',
        status: hypervisor.status || 'active', notes: hypervisor.notes || '',
        cpu_total_cores: hypervisor.cpu_total_cores ?? '', cpu_model: hypervisor.cpu_model || '',
        ram_total_gb: hypervisor.ram_total_gb ?? '', storage_local_tb: hypervisor.storage_local_tb ?? '',
        rack_element_id: hypervisor.rack_element_id ?? '',
      });
    } else {
      onClose?.();
    }
    setIsDirty(false);
    setErrors([]);
    setApiError('');
  };

  const handleSubmit = async (e, { keepEditing = false } = {}) => {
    if (e) e.preventDefault();
    const errs = [];
    if (!form.name.trim()) errs.push('El nombre es obligatorio');
    if (errs.length) { setErrors(errs); return false; }

    setSaving(true);
    setApiError('');
    try {
      const payload = {
        ...form,
        cpu_total_cores:  form.cpu_total_cores  !== '' ? Number(form.cpu_total_cores)   : null,
        ram_total_gb:     form.ram_total_gb      !== '' ? Number(form.ram_total_gb)      : null,
        storage_local_tb: form.storage_local_tb  !== '' ? parseFloat(form.storage_local_tb) : null,
        rack_element_id:  form.rack_element_id   !== '' ? Number(form.rack_element_id)   : null,
      };
      let creado = null;
      if (isEditing) {
        await api.updateHypervisor(hypervisor.id, payload, companyId);
      } else {
        creado = registroGuardado(await api.createHypervisor(payload, companyId));
      }
      setIsDirty(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      if (isCards && !keepEditing) setEditing(false);
      return true;
    } catch (err) {
      setApiError(err.message || 'Error guardando hypervisor');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'general',  label: isCards ? 'Resumen' : 'General' },
    { key: 'hardware', label: 'Hardware' },
    ...(isEditing ? [{ key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` }] : []),
    { key: 'vms',      label: `Máquinas Virtuales${vms.length ? ` (${vms.length})` : ''}` },
  ];
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;

  const VM_STATUS = {
    on:        { bg: 'rgba(16,185,129,.15)',  color: '#6ee7b7', label: 'On' },
    off:       { bg: 'rgba(239,68,68,.15)',   color: '#fca5a5', label: 'Off' },
    suspended: { bg: 'rgba(234,179,8,.15)',   color: '#fde68a', label: 'Susp.' },
  };

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Hypervisores
        </button>
        <DetailCrumb label={form.name} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Hipervisor"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {(apiError || errors.length > 0) && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError || errors[0]}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon /> Cerrar
            </button>
          ) : (
          <>
          <span className="dv-status-badge" style={{ background: currentStatus.bg, color: currentStatus.color }}>
            ● {currentStatus.label}
          </span>
          {!editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon /> Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon /> Editar
              </button>
            </>
          ) : (
            <>
              {isDirty && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
                  <XMarkIcon />
                  Descartar
                </button>
              )}
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={handleSubmit}
                disabled={saving || !isDirty}
              >
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        {/* Tipo de hypervisor */}
        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('general')}
          title="Tipo de hypervisor"
        >
          <span className="dv-smart-btn-icon">
            <CpuChipIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.8rem' }}>
            {typeInfo.icon} {typeInfo.label.split(' ')[0]}
          </span>
          <span className="dv-smart-btn-label">Tipo</span>
        </button>

        {/* IP gestión */}
        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('general')}
          title="IP de gestión"
        >
          <span className="dv-smart-btn-icon">
            <GlobeAltIcon style={{ width: 18, height: 18, color: '#0369a1' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#0369a1', fontSize: '0.75rem', fontFamily: 'monospace' }}>
            {form.mgmt_ip || '—'}
          </span>
          <span className="dv-smart-btn-label">IP Gestión</span>
        </button>

        {/* Servidor físico */}
        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('hardware')}
          title="Servidor físico vinculado"
        >
          <span className="dv-smart-btn-icon">
            <ServerStackIcon style={{ width: 18, height: 18, color: selectedServer ? '#059669' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{
            color: selectedServer ? '#059669' : '#94a3b8',
            fontSize: selectedServer ? '0.78rem' : '0.75rem',
            maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {selectedServer?.name || 'Sin vincular'}
          </span>
          <span className="dv-smart-btn-label">Servidor</span>
        </button>

        {/* Hardware */}
        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('hardware')}
          title="Especificaciones hardware"
        >
          <span className="dv-smart-btn-icon">
            <WrenchScrewdriverIcon style={{ width: 18, height: 18, color: '#6b7280' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#6b7280', fontSize: '0.75rem' }}>
            {hwSummary || '—'}
          </span>
          <span className="dv-smart-btn-label">Hardware</span>
        </button>

        {/* VMs */}
        {isEditing && (
          <button
            className="dv-smart-btn"
            type="button"
            onClick={() => setActiveTab('vms')}
            title="Máquinas virtuales en este host"
          >
            <span className="dv-smart-btn-icon">
              <ComputerDesktopIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed' }}>
              {vms.length}
            </span>
            <span className="dv-smart-btn-label">VMs</span>
          </button>
        )}

        {/* HA mode */}
        <button
          className="dv-smart-btn"
          type="button"
          onClick={() => setActiveTab('general')}
          title="Modo HA"
        >
          <span className="dv-smart-btn-value" style={{ fontSize: '0.8rem', color: form.ha_mode === 'cluster_member' ? '#2563eb' : '#6b7280' }}>
            {form.ha_mode === 'cluster_member' ? '🔗 Cluster' : '◎ Standalone'}
          </span>
          <span className="dv-smart-btn-label">Modo HA</span>
        </button>
        {hypervisor?.id && (
          <DocSmartButton elementType="virt_hypervisor" elementId={hypervisor.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={handleSubmit}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<CpuChipIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => sf('name', v)}
                placeholder="Nombre del hypervisor…"
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                crumb={[typeInfo?.label || form.hypervisor_type, form.version, form.ha_mode === 'cluster_member' ? 'Cluster' : 'Standalone'].filter(Boolean).join(' › ')}
                badges={[
                  { text: currentStatus.label, color: form.status === 'active' ? '#16a34a' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                  { text: form.ha_mode === 'cluster_member' ? 'Cluster HA' : 'Standalone' },
                ]}
                chips={[
                  ...(form.cpu_cores ? [{ value: `${form.cpu_cores} cores`, label: 'CPU' }] : []),
                  ...(form.ram_gb ? [{ value: `${form.ram_gb} GB`, label: 'RAM' }] : []),
                  { value: vms.length, label: 'VMs', onClick: () => setActiveTab('vms') },
                  ...(accessCount > 0 ? [{ value: accessCount, label: 'accesos', onClick: () => setActiveTab('access') }] : []),
                ]}
                extra={hypervisor?.id ? (
                  <DocSmartButton elementType="virt_hypervisor" elementId={hypervisor.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
              <input
                className="dv-record-title"
                value={form.name}
                onChange={e => sf('name', e.target.value)}
                placeholder="Nombre del hypervisor…"
                required
              />
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabs.map(tab => (
                  <TabButton
                    key={tab.key}
                    type="button"
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing}>

          {/* ── Tab: General ── */}
          {activeTab === 'general' && (
            <div className={paneClass}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Tipo</span><b style={{ fontSize: '0.95rem' }}>{typeInfo.label}</b></div>
                  <div className="sf2-kpi"><span>Versión</span><b style={{ fontSize: '0.95rem' }}>{form.version || '—'}</b></div>
                  <div className="sf2-kpi"><span>VMs</span><b>{vms.length}</b></div>
                  <div className="sf2-kpi"><span>Modo</span><b style={{ fontSize: '0.95rem' }}>{form.ha_mode === 'cluster_member' ? 'Cluster' : 'Standalone'}</b></div>
                  <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{currentStatus.label}</b></div>
                </div>
              )}
              <div className="dv-section">
                <div className="dv-section-title">Identificación</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Tipo de hypervisor <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={form.hypervisor_type}
                      onChange={e => sf('hypervisor_type', e.target.value)}
                    >
                      {HYP_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Versión</label>
                    <input
                      className="form-control"
                      value={form.version}
                      onChange={e => sf('version', e.target.value)}
                      placeholder="ej. 8.0.2"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Modo HA</label>
                    <select
                      className="form-select"
                      value={form.ha_mode}
                      onChange={e => sf('ha_mode', e.target.value)}
                    >
                      <option value="standalone">Standalone</option>
                      <option value="cluster_member">Miembro de cluster</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-select"
                      value={form.status}
                      onChange={e => sf('status', e.target.value)}
                    >
                      <option value="active">✅ Activo</option>
                      <option value="maintenance">🔧 Mantenimiento</option>
                      <option value="decom">🚫 Decom</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Acceso de gestión</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">IP de gestión</label>
                    <input
                      className="form-control"
                      value={form.mgmt_ip}
                      onChange={e => sf('mgmt_ip', e.target.value)}
                      placeholder="ej. 10.0.0.10"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                  <div className="col-md-9">
                    <label className="form-label">URL de gestión</label>
                    <input
                      className="form-control"
                      value={form.mgmt_url}
                      onChange={e => sf('mgmt_url', e.target.value)}
                      placeholder="ej. https://esxi-prod-01.local/ui"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Hardware ── */}
          {activeTab === 'hardware' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Servidor físico</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Servidor (rack element)</label>
                    <select
                      className="form-select"
                      value={form.rack_element_id}
                      onChange={e => sf('rack_element_id', e.target.value)}
                    >
                      <option value="">— Sin vincular —</option>
                      {servers.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.ip_address ? ` · ${s.ip_address}` : ''}{s.rack_name ? ` | ${s.rack_name}` : ''}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Vincula este hypervisor con su servidor físico inventariado en Infraestructura.
                    </small>
                  </div>
                  {selectedServer && (
                    <div className="col-md-6">
                      <div style={{
                        background: 'rgba(16,185,129,.15)', border: '1px solid rgba(16,185,129,.4)', borderRadius: 8,
                        padding: '10px 14px', fontSize: 13,
                      }}>
                        <div style={{ fontWeight: 600, color: '#15803d' }}>{selectedServer.name}</div>
                        {selectedServer.manufacturer && <div style={{ color: '#374151' }}>{selectedServer.manufacturer} {selectedServer.model}</div>}
                        {selectedServer.ip_address && <div style={{ fontFamily: 'monospace', color: '#6b7280', fontSize: 12 }}>{selectedServer.ip_address}</div>}
                        {selectedServer.rack_name && <div style={{ color: '#6b7280', fontSize: 12 }}>📍 {selectedServer.rack_name}</div>}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Especificaciones del host</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Cores totales (CPU)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.cpu_total_cores}
                      onChange={e => sf('cpu_total_cores', e.target.value)}
                      placeholder="ej. 64"
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Modelo de CPU</label>
                    <input
                      className="form-control"
                      value={form.cpu_model}
                      onChange={e => sf('cpu_model', e.target.value)}
                      placeholder="ej. Intel Xeon Gold 6338"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">RAM total (GB)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.ram_total_gb}
                      onChange={e => sf('ram_total_gb', e.target.value)}
                      placeholder="512"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Storage local (TB)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      value={form.storage_local_tb}
                      onChange={e => sf('storage_local_tb', e.target.value)}
                      placeholder="3.84"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* ── Tab: Accesos ── */}
          {activeTab === 'access' && isEditing && (
            <div className={paneClass}>
              <DeviceAccessPanel
                objectType="virt_hypervisor"
                objectId={hypervisor?.id}
                companyId={companyId}
                elementName={hypervisor?.name || form.name}
                defaultHost={form.mgmt_ip}
                onCountChange={setAccessCount}
                canEdit={editing}
              />
            </div>
          )}

          {/* ── Tab: Máquinas Virtuales ── */}
          {activeTab === 'vms' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">VMs alojadas en este hypervisor</div>
                {!isEditing ? (
                  <p style={{ color: '#9ca3af', fontSize: 13 }}>Guarda primero el hypervisor para ver sus VMs.</p>
                ) : vms.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: 13 }}>No hay máquinas virtuales registradas en este host.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        {['Nombre', 'SO', 'CPU / RAM', 'Estado', 'Tipo'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: '#6b7280', fontWeight: 500, fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {vms.map(vm => {
                        const st = VM_STATUS[vm.power_state] || { bg: '#f3f4f6', color: '#6b7280', label: vm.power_state };
                        return (
                          <tr key={vm.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '7px 10px', fontWeight: 600 }}>{vm.name}</td>
                            <td style={{ padding: '7px 10px', color: '#6b7280' }}>{vm.os_name || '—'}{vm.os_version ? ` ${vm.os_version}` : ''}</td>
                            <td style={{ padding: '7px 10px', color: '#6b7280' }}>
                              {vm.cpu_cores ? `${vm.cpu_cores} vCPU` : '—'}
                              {vm.ram_gb ? ` · ${vm.ram_gb} GB` : ''}
                            </td>
                            <td style={{ padding: '7px 10px' }}>
                              <span style={{ background: st.bg, color: st.color, borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>{st.label}</span>
                            </td>
                            <td style={{ padding: '7px 10px', color: '#9ca3af', fontSize: 12 }}>{vm.vm_type || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit(null)}
          />
          </FormCardsShell>
        </form>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="virt_hypervisor"
          ciId={hypervisor?.id}
          tipoLegible="el hipervisor"
        />
      </div>
    </div>
  );
}
